/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ToasterService } from '../../api/toaster.service';
import { AuthService } from 'src/app/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { CancelConfirmationDialogComponent } from 'src/app/components/dialogs/cancel-confirmation-dialog/cancel-confirmation-dialog.component';
import { CommonMessages } from 'src/app/constants/common-messages';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private isRefreshingToken = false;
  private connectivityIssuesDialogId = 'connectivity-issues-dialog';

  constructor(private toasterService: ToasterService, private authService: AuthService, private dialog: MatDialog) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // 401 - refresh token
        if (error.status === 401) {
          return this.handleUnAuthorizedRequest(request, next);
        }
         if (error.status === 417) {
           const errorMessage = this.formatErrorMessage(error);
           this.toasterService.error(errorMessage);
           return throwError(() => error);
        }

        if (error.error?.includes('Forbidden (Status Code: 403)')) {
          return this.authService.loginPopup().pipe(
            catchError(() => {
              return this.authService.getNewToken();
            }),
            switchMap((newToken) => {
              const retryReq = request.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
              });
              return next.handle(retryReq);
            })
          );
        }

        if ([0, 511].includes(error.status)) {
          return this.handleConnectivityIssuesRequest(next);
        }

        // all other errors
        const errorMessage = this.formatErrorMessage(error);
        this.toasterService.error(errorMessage);

        return throwError(() => error);
      })
    );
  }

  private handleUnAuthorizedRequest(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.authService.refreshToken();
    }

    return this.authService.refreshTokenStatus().pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        this.isRefreshingToken = false;
        return next.handle(this.addToken(request, token));
      }),
      catchError((err) => {
        this.isRefreshingToken = false;
        return throwError(() => err);
      })
    );
  }

  private handleConnectivityIssuesRequest(next: HttpHandler): Observable<HttpEvent<any>> {
    const isDialogOpen = this.dialog.openDialogs?.find((x) => x.id === this.connectivityIssuesDialogId);
    if (isDialogOpen) {
      return throwError(() => next);
    }

    const dialogRef = this.dialog.open(CancelConfirmationDialogComponent, {
      id: this.connectivityIssuesDialogId,
      disableClose: true,
      width: '45%',
      data: {
        title: 'Connectivity Issues',
        description: 'Please check your connection and click on "continue", or try to "login again".',
        confirmButtonText: 'Login again',
        continueButtonText: CommonMessages.BUTTON_TEXT_CONTINUE,
        action: 'Login again',
      },
    });
    dialogRef.afterClosed().subscribe((confirm) => {
      if (confirm) {
        this.authService.signOut();
      }
    });

    return throwError(() => next);
  }

  private addToken(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }
    return request;
  }

  private formatErrorMessage(error: HttpErrorResponse): string {
    if (error.error instanceof ErrorEvent) {
      return `Error: ${error.error.message}`;
    }

    // Handle Blob/ArrayBuffer error (from binary responseType)
    if (error.error instanceof Blob || error.error instanceof ArrayBuffer) {
      return 'No file(s) available for download';
    }

    const containsCustomApiError = error.error != null && error.error.length > 0 && typeof error.error[0] === 'string';
    return containsCustomApiError ? error.error : this.getDefaultMessageFromHttpErrorResponse(error);
  }

  private getDefaultMessageFromHttpErrorResponse(error: HttpErrorResponse): string {
    switch (error.status) {
      case 400:
        {
          let errorMessage = 'Invalid request';
          if (error.error && error.error.errors) {
            for (const key in error.error.errors) {
              if (error.error.errors.hasOwnProperty(key)) {
                errorMessage += `\n ${key} - ${error.error.errors[key].join('\n- ')}`;
              }
            }
          }
          return errorMessage;
        }
      case 404:
        return 'Item not found';
      case 405:
        return 'Method Not Allowed';
      case 401:
        return 'Invalid credentials. Please clear the cache and refresh the page.';
      case 403:
        return 'You do not have sufficient permissions to perform the requested action';
        break;
      case 500:
        return 'The server encountered an internal error and was unable to complete your request.\nPlease contact the system administrator.';
      case 502:
      case 504:
        return 'The server encountered a temporary error and could not complete your request.\nPlease try again in sometime.';
      default:
        return `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
  }
}
